import * as React from 'react'
import { cn } from '@/lib/utils'

/**
 * Canonical marketing form field styling. One recipe for the inputs that
 * footer/contact/PilotCTA/BookCallCTA previously each hand-rolled with
 * drifting radii, focus rings, and placeholder colors.
 *
 * Rules baked in: sharp corners, --control-border boundary (3:1 non-text
 * contrast), keyboard-visible focus ring, tokenized placeholder.
 *
 * `marketingFieldClasses` is exported so textarea/select fields can share the
 * exact recipe without a wrapper component per element type.
 */
export function marketingFieldClasses(opts?: {
  /** 'inverse' is for always-dark bands (footer, dark CTA sections). */
  tone?: 'default' | 'inverse'
  /** Taller capture-form fields (PilotCTA/BookCallCTA). */
  size?: 'default' | 'lg'
  invalid?: boolean
}): string {
  const { tone = 'default', size = 'default', invalid = false } = opts ?? {}
  return cn(
    'w-full rounded-none transition-colors text-sm focus:outline-none focus-visible:ring-2 disabled:opacity-50',
    size === 'lg' ? 'min-h-[52px] px-4' : 'px-4 py-3',
    tone === 'inverse'
      ? 'bg-white/10 border border-white/45 text-white placeholder:text-white/40 focus:border-[var(--secondary)] focus-visible:ring-[var(--secondary)]'
      : 'bg-background border border-[var(--control-border)] text-foreground placeholder:text-muted-foreground focus:border-primary focus-visible:ring-ring',
    invalid && 'border-destructive focus-visible:ring-destructive'
  )
}

export interface MarketingInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  tone?: 'default' | 'inverse'
  fieldSize?: 'default' | 'lg'
  invalid?: boolean
}

export const Input = React.forwardRef<HTMLInputElement, MarketingInputProps>(
  ({ tone, fieldSize, invalid, className, ...props }, ref) => (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        marketingFieldClasses({ tone, size: fieldSize, invalid }),
        className
      )}
      {...props}
    />
  )
)
Input.displayName = 'MarketingInput'

export default Input
